import React, { useEffect, useState, useRef, useCallback } from "react";
import { ethers } from "ethers";
import "../../gamble.css";

import tournamentAbi from "../../abi/tournament_abi.json";
import QRCode from "../../QRCodeGenerator.js";

const RPCS = [
  "https://api.avax.network/ext/bc/C/rpc",
  "https://avalanche-c-chain.publicnode.com",
];

const CONTRACTS = {
  V1: {
    address: "0xfB2348E6E883672698F6877fD45f4F113204DDbA",
    label: "2 Step / 4 Players",
    terms: "300",
    winners: 1,
    actualBet: 279.0207,
    estimatedPool: 1116.0828,
  },

  V2: {
    address: "0x7Ec7010A3Ad8177Ea4c242ce738E36751D6DDd0e",
    label: "2 Step / 4 Players",
    terms: "600",
    winners: 1,
    actualBet: 558.0414,
    estimatedPool: 2232.1656,
  },

  V3: {
    address: "0xC57A5606b83A3EbA90aAFfBb111f9b04DF15a1Be",
    label: "2 Step / 4 Players",
    terms: "900",
    winners: 1,
    actualBet: 837.0621,
    estimatedPool: 3348.2484,
  },
};

// const EXPLORER = `https://snowtrace.io/address/${CONTRACTS.address}`;

const short1 = (addr) => `${addr.slice(0, 10)}...${addr.slice(-10)}`;
const short2 = (addr) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;
const addressLink = (addr) => `https://snowtrace.io/address/${addr}`;

export default function Gamble() {
  const [data, setData] = useState({
    V1: {
      state: null,
      players: [],
      previousCycles: [],
    },
    V2: {
      state: null,
      players: [],
      previousCycles: [],
    },

    V3: {
      state: null,
      players: [],
      previousCycles: [],
    },
  });
  const [price, setPrice] = useState(null);
  const [isQRCodeVisible, setIsQRCodeVisible] = useState(false);
  const [currentQRCodeData, setCurrentQRCodeData] = useState("");
  const [copied, setCopied] = useState(false);

  const providerRef = useRef(null);
  // const contractRef = useRef(null);

  const copy = async (address) => {
    await navigator.clipboard.writeText(address);

    setCopied(address);

    setTimeout(() => {
      setCopied(false);
    }, 1200);
  };

  const handleShowQRCode = (contractAddress) => {
    setCurrentQRCodeData(contractAddress);
    setIsQRCodeVisible(true);
  };

  const handleCloseQRCode = () => {
    setIsQRCodeVisible(false);
    setCurrentQRCodeData("");
  };

  const TokenLogo = ({ size = 12 }) => (
    <img
      src="/logo.svg"
      alt="token logo"
      style={{
        width: `${size}px`,
        verticalAlign: "middle",
        marginLeft: "2px",
      }}
    />
  );

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const provider = new ethers.FallbackProvider(
          RPCS.map((url) => new ethers.JsonRpcProvider(url)),
        );

        providerRef.current = provider;

        const contracts = {
          V1: new ethers.Contract(
            CONTRACTS.V1.address,
            tournamentAbi,
            provider,
          ),
          V2: new ethers.Contract(
            CONTRACTS.V2.address,
            tournamentAbi,
            provider,
          ),

          V3: new ethers.Contract(
            CONTRACTS.V3.address,
            tournamentAbi,
            provider,
          ),
        };

        const loadOneContract = async (key, contract) => {
          try {
            const [bet, max, count, pool, cycle, stateEnum] = await Promise.all(
              [
                contract.BET(),
                contract.MAX_PLAYERS(),
                contract.playersCount(),
                contract.pool(),
                contract.cycleId(),
                contract.state(),
              ],
            );

            const playersArr = await Promise.all(
              Array.from({ length: Number(count) }, (_, i) =>
                contract.players(i),
              ),
            );

            const latest = await provider.getBlockNumber();

            const iface = new ethers.Interface(tournamentAbi);

            const topicRound = iface.getEvent("RoundResolved").topicHash;

            const topicFinished = iface.getEvent("Finished").topicHash;

            const CHUNK = 10;

            const START_BLOCK = Math.max(0, latest - 4000);

            let roundRawLogs = [];

            let finishRawLogs = [];

            for (let from = START_BLOCK; from <= latest; from += CHUNK) {
              const to = Math.min(from + CHUNK - 1, latest);

              const roundLogsChunk = await provider.getLogs({
                address: CONTRACTS[key].address,
                fromBlock: from,
                toBlock: to,
                topics: [topicRound],
              });

              const finishLogsChunk = await provider.getLogs({
                address: CONTRACTS[key].address,
                fromBlock: from,
                toBlock: to,
                topics: [topicFinished],
              });

              roundRawLogs.push(...roundLogsChunk);

              finishRawLogs.push(...finishLogsChunk);
            }

            const cycles = {};

            roundRawLogs.forEach((raw) => {
              const log = iface.parseLog(raw);

              const cycleId = Number(log.args.cycleId);

              if (!cycles[cycleId]) {
                cycles[cycleId] = {
                  rounds: {},
                };
              }

              cycles[cycleId].rounds[Number(log.args.step)] = {
                winners: [...log.args.winners],
                losers: [...log.args.losers],
              };
            });

            finishRawLogs.forEach((raw) => {
              const log = iface.parseLog(raw);

              const cycleId = Number(log.args.cycleId);

              if (!cycles[cycleId]) {
                cycles[cycleId] = {
                  rounds: {},
                };
              }

              cycles[cycleId].winner = log.args.winner;

              cycles[cycleId].prize = ethers.formatEther(log.args.prize);
            });

            const parsedCycles = Object.entries(cycles)
              .map(([id, data]) => ({
                id: Number(id),
                ...data,
              }))
              .sort((a, b) => b.id - a.id)
              .slice(0, 5);

            if (!mounted) return;

            setData((prev) => ({
              ...prev,

              [key]: {
                state: {
                  bet: ethers.formatEther(bet),
                  max: max.toString(),
                  count: count.toString(),
                  pool: ethers.formatEther(pool),
                  cycleId: cycle.toString(),

                  state: Number(stateEnum) === 0 ? "OPEN" : "LOCKED",
                },

                players: playersArr,

                previousCycles: parsedCycles,
              },
            }));
          } catch (err) {
            console.error(`load ${key} error:`, err);
          }
        };

        const loadAll = async () => {
          await Promise.all([
            loadOneContract("V1", contracts.V1),
            loadOneContract("V2", contracts.V2),
            loadOneContract("V3", contracts.V3),
          ]);
        };

        await loadAll();

        const interval = setInterval(async () => {
          await loadAll();
        }, 10000);

        return () => clearInterval(interval);
      } catch (err) {
        console.error("init error:", err);
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, []);

  const loadPrice = useCallback(async () => {
    try {
      const res = await fetch(
        "https://api.dexscreener.com/latest/dex/tokens/0xc45d82628E0698404271B3C9Cb6DC2d77efd9346",
      );
      const data = await res.json();

      if (data.pairs && data.pairs.length > 0) {
        setPrice(data.pairs[0].priceUsd);
      }
    } catch (e) {
      console.warn("Price load failed", e);
    }
  }, []);

  // ✅ effect (NO WARNINGS NOW)
  useEffect(() => {
    loadPrice();

    const interval = setInterval(() => {
      loadPrice();
    }, 10000);

    return () => clearInterval(interval);
  }, [loadPrice]);

  const renderTournament = (key, config, tournamentData) => {
    const state = tournamentData.state;

    const players = tournamentData.players;

    const previousCycles = tournamentData.previousCycles;

    const EXPLORER = `https://snowtrace.io/address/${config.address}`;

    const primaryBetUsd = price
      ? (Number(config.terms) * price).toFixed(6)
      : null;

    const poolUsd = price
      ? (Number(config.estimatedPool) * price).toFixed(6)
      : null;
    // ---------------- UI ----------------

    return (
      <div className="gamble-wrapper">
        <div className="gamble-content1">
          <div className="gambleRow">
            <h5>{config.label}</h5>
          </div>
          <div className="gamble-content12">
            <h5>
              {config.terms}
              <TokenLogo size={15} /> ={" "}
              {primaryBetUsd
                ? `$${Number(primaryBetUsd).toFixed(6)}`
                : "Loading..."}
            </h5>
            <div className="gambleRow">
              <a
                href={EXPLORER}
                target="_blank"
                rel="noopener noreferrer"
                className="address-link"
              >
                <div className="gambleRow">{config.address}</div>
              </a>
            </div>

            <div className="headerButtonRow">
              {copied === config.address && (
                <span className="copy-msg-inline">Copied</span>
              )}

              <button
                onClick={() => copy(config.address)}
                className="copyButton"
                title="Copy address"
              >
                📋
              </button>

              <button
                onClick={() => {
                  handleShowQRCode(config.address);
                }}
                className="copyButton"
              >
                🔳
              </button>
            </div>

            {isQRCodeVisible && currentQRCodeData === config.address && (
              <div className="qr-code-modal">
                <div className="qr-code-container">
                  <h6 style={{ color: "black" }}>{config.address}</h6>

                  <div className="gettoHeaderRowSub">
                    <p>
                      To join the tournament you just need to send{" "}
                      {config.terms} <TokenLogo /> to the contract address.
                    </p>
                  </div>

                  <QRCode
                    address={config.address}
                    id={`qr-${config.address}`}
                  />

                  <button onClick={handleCloseQRCode}>Close</button>
                </div>
              </div>
            )}
          </div>

          <div className="gamble-content11">
            {state ? (
              <>
                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h6>Cycle:</h6>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h5>{state.cycleId}</h5>
                  </div>
                </div>

                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h6>Winner:</h6>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h5>{config.winners}</h5>
                  </div>
                </div>

                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h6>Players:</h6>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h5>
                      {state.count} / {state.max}
                    </h5>
                  </div>
                </div>

                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h6> to JOIN: </h6>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h5>
                      send {config.terms}
                      <TokenLogo size={16} />
                    </h5>
                  </div>
                </div>
                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h6> Actual Bet:</h6>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h5>
                      {config.actualBet}
                      <TokenLogo size={16} />
                    </h5>
                  </div>
                </div>

                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h6>Pool:</h6>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h5
                      title={
                        poolUsd
                          ? `Estimated Pool USD Value: $${poolUsd}`
                          : "Loading USD value..."
                      }
                      style={{ cursor: "help" }}
                    >
                      {state.pool} / {config.estimatedPool}
                      <TokenLogo size={16} />
                    </h5>
                  </div>
                </div>
              </>
            ) : (
              <p>Loading...</p>
            )}
          </div>
        </div>
        <div className="gamble-content1112">
          <div className="gambleCenteredRowHeader">
            <h5>Current Players</h5>
          </div>
          <div className="gamble-content123">
            <div className="gambleList">
              {players.length === 0 && <p>No players yet</p>}

              {players.map((p, i) => (
                <div className="gambleRow" key={p}>
                  <p>
                    {i + 1}:{" "}
                    <a
                      href={addressLink(p)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="address-link"
                    >
                      {short1(p)}
                    </a>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="gamble-content1112">
          <div className="gambleCenteredRowHeader">
            <h5>Previous Cycles</h5>
          </div>
          <div className="gamble-content111">
            {previousCycles.length === 0 && (
              <div className="gamblePlaceholder">
                <p>No previous cycles found</p>
              </div>
            )}

            {previousCycles.map((cycle) => (
              <div
                key={cycle.id}
                className="gambleBlock"
                style={{ marginBottom: "25px" }}
              >
                <div className="gambleCenteredRow">
                  <h5>CYCLE #{cycle.id}</h5>
                </div>

                {/* ONE WINNER */}
                {cycle.winner && (
                  <>
                    <div className="gambleHeaderRow">
                      <div className="gambleRow">
                        <h5>Winner:</h5>
                      </div>

                      <div className="gettoHeaderRowSub">
                        <a
                          href={addressLink(cycle.winner)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="address-link"
                        >
                          {short2(cycle.winner)}
                        </a>
                      </div>
                    </div>

                    <div className="gambleHeaderRow">
                      <div className="gambleRow">
                        <h5>Prize:</h5>
                      </div>

                      <div className="gettoHeaderRowSub">
                        <p>
                          {cycle.prize} <TokenLogo size={16} />
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* TWO WINNERS */}
                {cycle.champion && (
                  <>
                    <div className="gambleHeaderRow">
                      <div className="gambleRow">
                        <h5>1st Place:</h5>
                      </div>

                      <div className="gettoHeaderRowSub">
                        <a
                          href={addressLink(cycle.champion)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="address-link"
                        >
                          {short2(cycle.champion)}
                        </a>
                      </div>
                    </div>

                    <div className="gambleHeaderRow">
                      <div className="gambleRow">
                        <h5>1st Place Prize:</h5>
                      </div>

                      <div className="gettoHeaderRowSub">
                        <p>
                          {cycle.championPrize} <TokenLogo size={16} />
                        </p>
                      </div>
                    </div>

                    <div className="gambleHeaderRow">
                      <div className="gambleRow">
                        <h5>2nd Place:</h5>
                      </div>

                      <div className="gettoHeaderRowSub">
                        <a
                          href={addressLink(cycle.runnerUp)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="address-link"
                        >
                          {short2(cycle.runnerUp)}
                        </a>
                      </div>
                    </div>

                    <div className="gambleHeaderRow">
                      <div className="gambleRow">
                        <h5>2nd Place Prize:</h5>
                      </div>

                      <div className="gettoHeaderRowSub">
                        <p>
                          {cycle.runnerUpPrize} <TokenLogo size={16} />
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {Object.entries(cycle.rounds)
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .map(([step, round]) => (
                    <div key={`${cycle.id}-${step}`}>
                      <div className="gambleRow">
                        <h5>Step {step}</h5>
                      </div>

                      {round.winners.map((w, i) => {
                        const l = round.losers[i];

                        return (
                          <div
                            className="gambleRow"
                            key={`${cycle.id}-${step}-${i}`}
                          >
                            <p>
                              <a
                                href={addressLink(w)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="address-link"
                              >
                                {short2(w)}
                              </a>{" "}
                              (W) -{" "}
                              <a
                                href={addressLink(l)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="address-link"
                              >
                                {short2(l)}
                              </a>{" "}
                              (L)
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="gamble-container">
      <div className="about-block">
        <div className="global-content2">
          <h4>2 STEP / 4 PLAYERS TOURNAMENT SMART CONTRACTS WITH 1 WINNER</h4>

          <p>
            The 2 step tournament consists of 4 players. To take part in
            tournament each player sends the exact bet amount to the smart
            contracts address, it receives minus 0.1%. Then 6.9% will be
            deducted to cover costs and ensure a profit, the rest will be added
            to the pot, and the players address will be automatically added to
            the players list. Whenever the last player joins, the contract will
            automatically determine the winner by getting random numbers from
            Chainlink VRF, shuffling the players list and sending the pot to the
            winners address. The first place address will receive 100% of the
            pot.
          </p>
        </div>
      </div>
      {renderTournament("V1", CONTRACTS.V1, data.V1)}
      {renderTournament("V2", CONTRACTS.V2, data.V2)}
      {renderTournament("V3", CONTRACTS.V3, data.V3)}
    </div>
  );
}
